import React, { useState } from 'react';

interface GemCardProps {
    title: string;
    icon: string;
    stocks: any[];
    theme: any;
    metricLabel: string;
    metricKey: string;
    type: 'yield' | 'momentum' | 'safe';
    criteria: string;
}

export const GemSection: React.FC<{ gems: any; theme: any }> = ({ gems, theme }) => {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            <GemCard
                title="Rois du Rendement"
                icon="💎"
                stocks={gems.yieldKings}
                theme={theme}
                metricLabel="Rendement"
                metricKey="div_yield"
                type="yield"
                criteria="Rendement ≥ 6.5% (sur 12 derniers mois) + Dividendes réguliers (≥3 ans) + Prix > MA50 (tendance haussière)"
            />
            <GemCard
                title="Meilleures Performances"
                icon="🚀"
                stocks={gems.momentumStars}
                theme={theme}
                metricLabel="YTD"
                metricKey="ytd"
                type="momentum"
                criteria="Performance YTD > 5% + RSI entre 50-75 (momentum sain) + Prix > MA20"
            />
            <GemCard
                title="Valeurs Refuges"
                icon="🛡️"
                stocks={gems.safeHavens}
                theme={theme}
                metricLabel="Volatilité"
                metricKey="vol30"
                type="safe"
                criteria="Volatilité < 20% + Dividendes réguliers (≥3 ans) + Performance YTD > -5%"
            />
        </div>
    );
};

const GemCard: React.FC<GemCardProps> = ({ title, icon, stocks, theme, metricLabel, metricKey, type, criteria }) => {
    const accentColor = type === 'yield' ? '#2962FF' : type === 'momentum' ? '#00b638' : '#FF6D00';
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div style={{
            backgroundColor: theme.boxBg,
            borderRadius: '12px',
            border: `1px solid ${type === 'yield' ? '#2962FF40' : type === 'momentum' ? '#00b63840' : '#FF6D0040'}`,
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
        }}>
            <div style={{
                padding: '16px',
                borderBottom: theme.border,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: `linear-gradient(to right, ${accentColor}10, transparent)`,
                position: 'relative'
            }}>
                <div style={{ fontSize: '24px' }}>{icon}</div>
                <div style={{ fontWeight: 'bold', fontSize: '18px', color: theme.text, flex: 1 }}>{title}</div>
                <div
                    style={{
                        cursor: 'pointer',
                        fontSize: '16px',
                        color: theme.sub,
                        position: 'relative'
                    }}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    ℹ️
                    {showTooltip && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: 8,
                            padding: '12px 16px',
                            background: '#1a1a2e',
                            color: '#fff',
                            borderRadius: 8,
                            fontSize: 12,
                            width: 250,
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            lineHeight: 1.5
                        }}>
                            <strong style={{ color: accentColor }}>Critères de sélection</strong>
                            <div style={{ marginTop: 6 }}>{criteria}</div>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ padding: '0' }}>
                {stocks.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: theme.sub, fontStyle: 'italic' }}>
                        Aucune opportunité détectée
                    </div>
                ) : (
                    stocks.map((s, i) => (
                        <div key={s.symbole} style={{
                            padding: '15px 20px',
                            borderBottom: i < stocks.length - 1 ? theme.border : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', color: theme.text }}>{s.symbole}</div>
                                <div style={{ fontSize: '11px', color: theme.sub }}>{s.nom}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', color: accentColor }}>
                                    {metricKey === 'vol30' ? ((s[metricKey] ?? 0) * 100).toFixed(2) : (s[metricKey] ?? 0).toFixed(2)}%
                                </div>
                                <div style={{ fontSize: '11px', color: theme.sub }}>{metricLabel}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
